import { useState } from "react";
import { FileText, FileImage, FileType2, File as FileIcon, LayoutGrid } from "lucide-react";
import { Button } from "./ui/button.js";
import { AllDocumentsDialog } from "./AllDocumentsDialog.js";

export interface DocSummary {
  id: string;
  type: string;
  name: string;
  mimeType: string | null;
  status: string;
  isVerified: boolean;
  expiresAt: string | null;
  rejectionReason: string | null;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  PAN: "PAN Card",
  AADHAAR: "Aadhaar",
  BANK_DOCUMENT: "Bank Statement",
  OTHER: "Resume",
  OFFER_LETTER: "Offer Letter",
  FORM_11: "Form 11",
  MARKSHEET: "Marksheet",
  DEGREE: "Degree",
};

export function docLabel(doc: DocSummary): string {
  return DOC_TYPE_LABELS[doc.type] ?? doc.name ?? doc.type;
}

function iconFor(mimeType: string | null) {
  if (!mimeType) return FileIcon;
  if (mimeType === "application/pdf") return FileText;
  if (mimeType.startsWith("image/")) return FileImage;
  if (mimeType.includes("word") || mimeType.includes("document")) return FileType2;
  return FileIcon;
}

function statusMeta(status: string): { color: string; label: string } {
  switch (status) {
    case "VERIFIED":
      return { color: "var(--color-success-fg)", label: "Verified" };
    case "REJECTED":
      return { color: "var(--color-error-fg)", label: "Rejected" };
    case "EXPIRED":
      return { color: "var(--color-warning-fg)", label: "Expired" };
    default:
      return { color: "var(--color-surface-400)", label: "Pending review" };
  }
}

export function DocumentsOverviewStrip({ docs, onOpen }: { docs: DocSummary[]; onOpen: (doc: DocSummary) => void }) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  if (docs.length === 0) return null;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px",
        background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)",
        borderRadius: "var(--radius-lg)", padding: "10px 16px",
        marginBottom: "var(--space-4)",
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-surface-400)", whiteSpace: "nowrap" }}>
        Glance
      </span>
      {docs.map((doc) => {
        const Icon = iconFor(doc.mimeType);
        const { color, label: statusLabel } = statusMeta(doc.status);
        const isHovered = hoveredId === doc.id;
        return (
          <div key={doc.id} style={{ position: "relative", flexShrink: 0 }}>
            <button
              type="button"
              onMouseEnter={() => setHoveredId(doc.id)}
              onMouseLeave={() => setHoveredId(null)}
              onFocus={() => setHoveredId(doc.id)}
              onBlur={() => setHoveredId(null)}
              onClick={() => onOpen(doc)}
              aria-label={`${docLabel(doc)} — ${statusLabel}`}
              style={{
                position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
                width: "34px", height: "34px", borderRadius: "var(--radius-md)",
                background: "var(--color-surface-50)", border: "1px solid var(--color-surface-100)",
                cursor: "pointer", color: "var(--color-surface-600)",
              }}
            >
              <Icon size={16} />
              <span
                style={{
                  position: "absolute", top: "-3px", right: "-3px",
                  width: "8px", height: "8px", borderRadius: "var(--radius-full)",
                  background: color, border: "1.5px solid var(--color-surface-0)",
                }}
              />
            </button>
            {isHovered && (
              <div
                role="tooltip"
                style={{
                  position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                  background: "#1f2937", color: "#fff",
                  fontSize: "11px", fontWeight: 500, whiteSpace: "nowrap",
                  padding: "4px 8px", borderRadius: "var(--radius-sm)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                  zIndex: 10, pointerEvents: "none",
                }}
              >
                {docLabel(doc)} · {statusLabel}
              </div>
            )}
          </div>
        );
      })}
      <Button type="button" variant="secondary" size="sm" className="ml-auto" onClick={() => setAllOpen(true)}>
        <LayoutGrid size={14} /> View all
      </Button>
      <AllDocumentsDialog docs={docs} open={allOpen} onOpenChange={setAllOpen} onOpenDoc={onOpen} />
    </div>
  );
}
